import { Box, Button, Flex, Grid, GridItem, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { ReactComponent as BackIcon } from 'assets/icons/chevron_left.svg';
import { useFetchTemplateData } from 'hooks/useFetchTemplateData/useFetchTemplateData';
import { E2_TEMPLATE_CATEGORY } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.config';
import { TemplateHit } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.types';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { isLoadingPage } from '../TemplatesMenu.helpers';
import { TemplatePage } from './TemplatePage';

/**
 * TEMPLATE PAGE VIEW
 *
 * Renders all page thumbs for a multipage template
 */
interface TemplatePageViewProps {
  templateData: TemplateHit;
  addPageIsDisabled: boolean;
  loadingTemplateId: string | null;

  // Triggered when clicking back button
  onExit: () => void;
  onAddPages: (templateData: TemplateHit, pageNumbers: number[]) => void;
}

const DISABLED_TOOLTIP_LABEL = "You've reached the maximum number of pages you can add onto the canvas.";

export const TemplatePageView = memo(
  ({ templateData, onExit, onAddPages, addPageIsDisabled, loadingTemplateId }: TemplatePageViewProps) => {
    const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.templatesMenu' });

    const { template_id: templateId, title, categories } = templateData;
    const { data, error } = useFetchTemplateData(templateId);
    const isAccessibleTemplate = categories.includes(E2_TEMPLATE_CATEGORY);

    if (!!error) {
      return <Box>Unable to fetch template data</Box>;
    }

    // Still fetching the template data
    if (!data) {
      return null;
    }

    // Shouldn't happen on PROD, but handle case when template data isn't in templates table
    if (!data.infograph) {
      return (
        <>
          <Button onClick={onExit} variant='outline' w='full' size='md' mr={2}>
            Go back
          </Button>
          <Text>Unable to fetch template data</Text>
        </>
      );
    }

    const {
      infograph: { pageOrder, size },
    } = data;
    const totalPages = pageOrder.length;
    // Display single column for landscape templates for better visibility
    const numColumns = size.widthPx > size.heightPx ? 1 : 2;

    const onSelectPage = (pageNumber: number) => {
      onAddPages(templateData, [pageNumber]);
    };

    const onAddAllPages = () => {
      onAddPages(
        templateData,
        pageOrder.map((_, index) => index + 1),
      );
    };

    return (
      <Box overflow='auto'>
        <Flex w='full' direction='column' overflow='hidden' h='full' p={2} gap={3}>
          {/* TEMPLATE TITLE + BACK BUTTON */}
          <Flex alignItems={'center'} w='full' h='30'>
            <IconButton
              variant={'icon-btn-side-menu'}
              size={'xs'}
              icon={<BackIcon />}
              aria-label={'Go back to templates list'}
              onClick={onExit}
              autoFocus={true}
              data-testid='template-page-view-back-btn'
            />
            <Text ml={2} fontSize='sm' color='font.500' fontWeight='semibold'>
              {title}
            </Text>
          </Flex>

          {/* ADD ALL PAGES */}
          <Tooltip
            isDisabled={!addPageIsDisabled}
            hasArrow
            label={DISABLED_TOOLTIP_LABEL}
            bg='black'
            textAlign='center'
            w={300}
          >
            <Button
              onClick={onAddAllPages}
              disabled={addPageIsDisabled}
              fontWeight='normal'
              variant='outline'
              p={3}
              borderRadius='base'
              size='sm'
              data-testid='template-page-view-add-all-btn'
              isLoading={loadingTemplateId === templateId}
              loadingText={t('preview.loadingTextMultiplePages')}
            >
              {t('preview.addAllPages')}
            </Button>
          </Tooltip>

          {/* PAGE PREVIEWS */}
          <Grid overflow='auto' flex='1' templateColumns={`repeat(${numColumns}, auto)`} gap={2}>
            {pageOrder.map((_, index) => {
              const pageNumber = index + 1;
              return (
                <GridItem key={`${templateId}-page-${pageNumber}`}>
                  <TemplatePage
                    addPageIsDisabled={addPageIsDisabled}
                    templateId={templateId}
                    pageNumber={pageNumber}
                    onSelectPage={onSelectPage}
                    isLoading={isLoadingPage(loadingTemplateId, templateId, pageNumber)}
                    isAccessibleTemplate={isAccessibleTemplate}
                    pageIndexStr={`${pageNumber}/${totalPages}`}
                  />
                </GridItem>
              );
            })}
          </Grid>
        </Flex>
      </Box>
    );
  },
);
TemplatePageView.displayName = 'TemplatePageView';
